import React from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { MapleLeaf } from '@/components/common/MapleLeaf';

import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Sheet, SheetClose, SheetContent } from '@/components/ui/sheet';

import { ChevronDown, Coins, Loader2, LogOut, Menu, Moon, Sun } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { useProfile } from '@/hooks/useProfile';
import { useAppStore } from '@/store';
import { supabase } from '@/integrations/supabase/client';
import { WhatsAppSupportSheet } from '@/components/support/WhatsAppSupportSheet';
import { WhatsAppSupportTrigger } from '@/components/support/WhatsAppSupportTrigger';
import { useToast } from '@/components/ui/use-toast';
import { cn } from '@/lib/utils';
import { useSupportPanelStore } from '@/store/useSupportPanelStore';

const productNavItems = [
  { label: 'CanXP Research', subtitle: 'AI Research Tool', href: '/research' },
  { label: 'CanXP MCP', subtitle: 'Connect Your Data', href: '/mcp' },
  { label: 'MapleOS', subtitle: 'AI OS', href: '/mapleos' },
  { label: 'NorthStar', subtitle: 'simulation', href: '/simulation' },
  { label: 'MapleNode', subtitle: 'Edge Cognition', href: '/maplenode' },
  { label: 'MaplePT', subtitle: 'AI Models', href: '/maplept' },
];

const servicesNavItems = [
  { label: 'Self-Serve', href: '/#self-serve' },
  { label: 'Enterprise', href: '/#services' },
];

const researchNavItems = [
  { label: 'Insights', href: '/insights' },
  { label: 'MaplePT', href: '/maplept' },
  { label: 'AI Sovereignty', href: '/sovereign-ai-canada' },
  { label: 'Reports', href: '/reports' },
  { label: 'News', href: '/news' },
];

const publicNavItems = [
  { label: 'Infrastructure', href: '/#sovereign-compute-network' },
  { label: 'Enterprise', href: '/enterprise' },
];

type CreditsNavLinkProps = {
  className?: string;
  onClick?: () => void;
};

function CreditsNavLink({ className, onClick }: CreditsNavLinkProps) {
  const { user, session } = useAuth();
  const [loading, setLoading] = React.useState(false);
  const [availableCredits, setAvailableCredits] = React.useState(0);

  const loadCredits = React.useCallback(async () => {
    if (!user || !session?.access_token) return;

    setLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke('get-ai-credit-account', {
        headers: { Authorization: `Bearer ${session.access_token}` },
      });

      if (error) throw error;
      setAvailableCredits(Number(data?.account?.availableCredits || 0));
    } catch {
      setAvailableCredits(0);
    } finally {
      setLoading(false);
    }
  }, [session?.access_token, user]);

  React.useEffect(() => {
    void loadCredits();
  }, [loadCredits]);

  React.useEffect(() => {
    const refresh = () => void loadCredits();
    window.addEventListener('mapleos:credits-refresh', refresh);
    return () => window.removeEventListener('mapleos:credits-refresh', refresh);
  }, [loadCredits]);

  if (!user) return null;

  return (
    <Link
      to="/credits"
      onClick={onClick}
      className={cn(
        'inline-flex items-center justify-center gap-2 rounded-full border border-border/70 bg-background/70 px-4 py-2 text-sm font-semibold text-foreground transition-colors hover:bg-accent',
        className,
      )}
    >
      {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Coins className="h-4 w-4" />}
      <span className="text-muted-foreground">{availableCredits.toLocaleString()}</span>
    </Link>
  );
}

const Header = () => {
  const { user, signOut } = useAuth();
  const { profile } = useProfile();
  const { darkMode, toggleDarkMode } = useAppStore();
  const navigate = useNavigate();
  const location = useLocation();
  const { toast } = useToast();
  const { open: supportOpen, setOpen: setSupportOpen } = useSupportPanelStore();
  const collapseDesktopHeader = supportOpen;
  const showCompactDesktopMenu = supportOpen;
  const [mobileMenuOpen, setMobileMenuOpen] = React.useState(false);

  const displayName = profile?.name || user?.email?.split('@')[0] || 'User';

  const avatarUrl = profile?.avatar;
  const initials = displayName
    .split(' ')
    .map((part) => part[0])
    .join('')
    .slice(0, 2)
    .toUpperCase();

  const handleSignOut = async () => {
    await signOut();
    navigate('/auth');
  };

  const handleLogoClick = (event: React.MouseEvent<HTMLAnchorElement>) => {
    event.preventDefault();

    if (location.pathname !== '/' || location.search || location.hash) {
      navigate('/');
    }

    window.requestAnimationFrame(() => {
      window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
    });
  };

  const handleSupportOpen = () => {
    if (!user) {
      toast({
        title: 'Log in to contact support',
        description: 'Please sign in first to open your support inbox and message our team.',
      });

    }

    setSupportOpen(true);
  };

  const handleMobileSupportOpen = () => {
    setMobileMenuOpen(false);
    handleSupportOpen();
  };

  return (

    <header className="sticky top-0 z-50 w-full border-b border-border/50 bg-background/90 backdrop-blur-xl supports-[backdrop-filter]:bg-background/70">
      <div className="container flex h-16 items-center justify-between gap-3 px-4">
        <div className={cn('flex min-w-0 flex-1 items-center gap-8', collapseDesktopHeader && 'gap-4')}>
          <Link to="/" onClick={handleLogoClick} className="flex shrink-0 items-center space-x-2">
            <MapleLeaf size={28} className="shrink-0 text-primary" />
            <span className={cn('whitespace-nowrap text-base font-bold text-foreground sm:text-lg', collapseDesktopHeader && 'hidden lg:inline')}>
              CanXP AI
            </span>
          </Link>

          <nav className={cn('hidden items-center gap-6', collapseDesktopHeader ? 'hidden' : 'md:flex')}>

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  type="button"
                  className="flex items-center gap-1 text-sm font-medium text-muted-foreground outline-none transition-colors hover:text-foreground focus:outline-none focus-visible:outline-none focus-visible:ring-0"
                >
                  Products
                  <ChevronDown className="h-4 w-4" />
                </button>
              </DropdownMenuTrigger>

              <DropdownMenuContent align="start" className="w-56">
                {productNavItems.map((item) => (
                  <DropdownMenuItem key={item.label} asChild className="items-start p-0">
                    <Link to={item.href} className="flex w-full flex-col items-start px-3 py-2 text-left leading-tight">
                      <span className="block text-sm font-medium">{item.label}</span>
                      <span className="mt-1 block text-xs text-muted-foreground">{item.subtitle}</span>
                    </Link>
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>

            </DropdownMenu>

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  type="button"
                  className="flex items-center gap-1 text-sm font-medium text-muted-foreground outline-none transition-colors hover:text-foreground focus:outline-none focus-visible:outline-none focus-visible:ring-0"
                >
                  AI Training
                  <ChevronDown className="h-4 w-4" />
                </button>
              </DropdownMenuTrigger>

              <DropdownMenuContent align="start" className="w-44">
                {servicesNavItems.map((item) => (
                  <DropdownMenuItem key={item.label} asChild>
                    <a href={item.href}>{item.label}</a>
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>

            <a href={publicNavItems[0].href} className="text-sm font-medium text-muted-foreground transition-colors hover:text-foreground">
              {publicNavItems[0].label}
            </a>

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  type="button"
                  className="flex items-center gap-1 text-sm font-medium text-muted-foreground outline-none transition-colors hover:text-foreground focus:outline-none focus-visible:outline-none focus-visible:ring-0"
                >
                  Research
                  <ChevronDown className="h-4 w-4" />
                </button>
              </DropdownMenuTrigger>

              <DropdownMenuContent align="start" className="w-56">
                {researchNavItems.map((item) => (
                  <DropdownMenuItem key={item.label} asChild>
                    <Link to={item.href}>{item.label}</Link>
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>

            <a href={publicNavItems[1].href} className="text-sm font-medium text-muted-foreground transition-colors hover:text-foreground">
              {publicNavItems[1].label}
            </a>

          </nav>

        </div>

        <nav className={cn('hidden items-center space-x-2 md:flex', collapseDesktopHeader && 'shrink-0')}>
          {showCompactDesktopMenu ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  type="button"
                  variant="outline"
                  size="icon"
                  aria-label="Open navigation menu"
                  className="hidden md:inline-flex"
                >
                  <Menu className="h-5 w-5" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-72">
                <DropdownMenuLabel>Navigation</DropdownMenuLabel>
                <DropdownMenuSeparator />
                <div className="px-2 py-1.5">
                  <p className="text-xs font-semibold uppercase tracking-[0.18em] text-muted-foreground">Products</p>
                </div>
                {productNavItems.map((item) => (
                  <DropdownMenuItem key={item.label} asChild className="items-start p-0">
                    <Link to={item.href} className="flex w-full flex-col items-start px-3 py-2 text-left leading-tight">
                      <span className="block text-sm font-medium">{item.label}</span>
                      <span className="mt-1 block text-xs text-muted-foreground">{item.subtitle}</span>
                    </Link>
                  </DropdownMenuItem>
                ))}
                <DropdownMenuSeparator />
                <div className="px-2 py-1.5">
                  <p className="text-xs font-semibold uppercase tracking-[0.18em] text-muted-foreground">AI Training</p>
                </div>
                {servicesNavItems.map((item) => (
                  <DropdownMenuItem key={item.label} asChild>
                    <a href={item.href}>{item.label}</a>
                  </DropdownMenuItem>
                ))}
                <DropdownMenuSeparator />
                <DropdownMenuItem asChild>
                  <a href={publicNavItems[0].href}>{publicNavItems[0].label}</a>
                </DropdownMenuItem>
                <div className="px-2 py-1.5">
                  <p className="text-xs font-semibold uppercase tracking-[0.18em] text-muted-foreground">Research</p>
                </div>
                {researchNavItems.map((item) => (
                  <DropdownMenuItem key={item.label} asChild>
                    <Link to={item.href}>{item.label}</Link>
                  </DropdownMenuItem>
                ))}
                <DropdownMenuItem asChild>
                  <a href={publicNavItems[1].href}>{publicNavItems[1].label}</a>
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => toggleDarkMode()}>
                  {darkMode ? <Sun className="mr-2 h-4 w-4" /> : <Moon className="mr-2 h-4 w-4" />}
                  {darkMode ? 'Light mode' : 'Dark mode'}
                </DropdownMenuItem>
                <DropdownMenuSeparator />

                {user ? (
                  <DropdownMenuItem asChild>
                    <Link to="/dashboard">Dashboard</Link>
                  </DropdownMenuItem>
                ) : (
                  <DropdownMenuItem asChild>
                    <Link to="/auth">Login</Link>
                  </DropdownMenuItem>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          ) : null}

          <WhatsAppSupportTrigger onClick={handleSupportOpen} aria-label="Open WhatsApp support chat" />

          {user ? (
            <>
              <CreditsNavLink className={cn(collapseDesktopHeader && 'hidden xl:inline-flex')} />

              <Button asChild variant="outline" size="sm" className={cn(collapseDesktopHeader && 'hidden xl:inline-flex')}>
                <Link to="/dashboard">Dashboard</Link>
              </Button>

              <Button
                type="button"
                variant={darkMode ? 'secondary' : 'ghost'}
                size="icon"
                onClick={() => toggleDarkMode()}
                aria-label={darkMode ? 'Switch to light mode' : 'Switch to dark mode'}
              >
                {darkMode ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
              </Button>

              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="icon" className="rounded-full">
                    <Avatar className="w-8 h-8 bg-muted">
                      <AvatarImage src={avatarUrl} alt={displayName} />
                      <AvatarFallback className="bg-muted text-xs font-semibold text-foreground">{initials}</AvatarFallback>
                    </Avatar>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <DropdownMenuLabel>
                    <div className="flex flex-col">
                      <span className="font-medium">{displayName}</span>
                      <span className="text-xs text-muted-foreground">{user.email}</span>
                    </div>
                  </DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => navigate('/profile')}>Profile</DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate('/settings')}>Settings</DropdownMenuItem>
                  <DropdownMenuItem onClick={handleSignOut}>
                    <LogOut className="mr-2 h-4 w-4" />
                    Logout
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </>
          ) : (
            <>
              <Button
                type="button"
                variant={darkMode ? 'secondary' : 'ghost'}
                size="icon"
                onClick={() => toggleDarkMode()}
                aria-label={darkMode ? 'Switch to light mode' : 'Switch to dark mode'}
              >
                {darkMode ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
              </Button>
              <Button asChild variant="ghost" size="sm" className={cn(collapseDesktopHeader && 'hidden xl:inline-flex')}>
                <Link to="/auth">Login</Link>
              </Button>
              <Button asChild size="sm" className={cn(collapseDesktopHeader && 'hidden xl:inline-flex')}>
                <Link to="/enterprise">Request a Demo</Link>
              </Button>
            </>
          )}

        </nav>

        <div className="flex items-center gap-2 md:hidden">
          {user ? (
            <Button asChild variant="ghost" size="sm">
              <Link to="/dashboard">Dashboard</Link>
            </Button>
          ) : (
            <Button asChild variant="ghost" size="sm">
              <Link to="/auth">Login</Link>
            </Button>
          )}

          <WhatsAppSupportTrigger onClick={handleSupportOpen} aria-label="Open WhatsApp support chat" />

          <Button variant="outline" size="icon" aria-label="Open menu" onClick={() => setMobileMenuOpen(true)}>
            <Menu className="h-5 w-5" />
          </Button>
        </div>
      </div>

      <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
        <SheetContent side="right" className="w-[85vw] max-w-sm overflow-y-auto border-border/60 bg-background/95 p-6">
          <div className="flex flex-col gap-6">
            <div>
              <p className="text-sm font-semibold uppercase tracking-[0.22em] text-primary">Menu</p>
              <div className="mt-4 flex flex-col gap-2">
                <p className="text-xs font-semibold uppercase tracking-[0.18em] text-muted-foreground">Products</p>
                {productNavItems.map((item) => (
                  <Link
                    key={item.label}
                    to={item.href}
                    className="block rounded-md px-3 py-2 text-left transition-colors hover:bg-accent"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    <span className="block text-sm font-medium text-foreground">{item.label}</span>
                    <span className="block text-xs text-muted-foreground">{item.subtitle}</span>
                  </Link>
                ))}
              </div>

              <div className="mt-6 flex flex-col gap-2">
                <p className="text-xs font-semibold uppercase tracking-[0.18em] text-muted-foreground">AI Training</p>
                {servicesNavItems.map((item) => (
                  <a
                    key={item.label}
                    href={item.href}
                    className="rounded-md px-3 py-2 text-sm font-medium text-foreground transition-colors hover:bg-accent"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    {item.label}
                  </a>
                ))}
              </div>
            </div>

            <div className="flex flex-col gap-2 border-t border-border/60 pt-4">
              <a
                href={publicNavItems[0].href}
                className="rounded-md px-3 py-2 text-sm font-medium text-foreground transition-colors hover:bg-accent"
                onClick={() => setMobileMenuOpen(false)}
              >
                {publicNavItems[0].label}
              </a>

              <div className="mt-2 flex flex-col gap-2">
                <p className="px-3 text-xs font-semibold uppercase tracking-[0.18em] text-muted-foreground">Research</p>
                {researchNavItems.map((item) => (
                  <Link
                    key={item.label}
                    to={item.href}
                    className="rounded-md px-3 py-2 text-sm font-medium text-foreground transition-colors hover:bg-accent"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    {item.label}
                  </Link>
                ))}
              </div>

              <a
                href={publicNavItems[1].href}
                className="rounded-md px-3 py-2 text-sm font-medium text-foreground transition-colors hover:bg-accent"
                onClick={() => setMobileMenuOpen(false)}
              >
                {publicNavItems[1].label}
              </a>
            </div>

            <div className="flex flex-col gap-3 border-t border-border/60 pt-4">
              {user ? (
                <>
                  <WhatsAppSupportTrigger showLabel className="justify-start" onClick={handleMobileSupportOpen}>
                    WhatsApp Support
                  </WhatsAppSupportTrigger>
                  <SheetClose asChild>
                    <CreditsNavLink className="w-full px-4 py-3" />
                  </SheetClose>
                  <Button asChild variant="outline">
                    <Link to="/dashboard" onClick={() => setMobileMenuOpen(false)}>Dashboard</Link>
                  </Button>

                  <Button
                    type="button"
                    variant={darkMode ? 'secondary' : 'ghost'}
                    onClick={() => toggleDarkMode()}
                    className="gap-2"
                    aria-label={darkMode ? 'Switch to light mode' : 'Switch to dark mode'}
                  >
                    {darkMode ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
                    {darkMode ? 'Light mode' : 'Dark mode'}
                  </Button>
                  <Button type="button" variant="ghost" onClick={() => { setMobileMenuOpen(false); navigate('/profile'); }}>
                    Profile
                  </Button>
                  <Button type="button" variant="ghost" onClick={() => { setMobileMenuOpen(false); navigate('/settings'); }}>
                    Settings
                  </Button>
                  <Button type="button" variant="ghost" onClick={handleSignOut} className="gap-2">
                    <LogOut className="h-4 w-4" />
                    Logout
                  </Button>
                </>
              ) : (
                <>
                  <WhatsAppSupportTrigger showLabel className="justify-start" onClick={handleMobileSupportOpen}>
                    WhatsApp Support
                  </WhatsAppSupportTrigger>
                  <Button
                        type="button"
                        variant={darkMode ? 'secondary' : 'ghost'}
                        onClick={() => toggleDarkMode()}
                        className="gap-2"
                        aria-label={darkMode ? 'Switch to light mode' : 'Switch to dark mode'}
                      >
                        {darkMode ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
                        {darkMode ? 'Light mode' : 'Dark mode'}
                      </Button>
                      <Button asChild variant="outline">
                        <Link to="/auth" onClick={() => setMobileMenuOpen(false)}>Login</Link>
                      </Button>
    
                      <Button asChild>
                        <Link to="/enterprise" onClick={() => setMobileMenuOpen(false)}>Request a Demo</Link>
                      </Button>
                    </>
                  )}
    
            </div>
          </div>
        </SheetContent>
      </Sheet>
      <WhatsAppSupportSheet open={supportOpen} onOpenChange={setSupportOpen} />

    </header>
  );

};

export { Header };
export default Header;
